import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import validator from 'validator';

import TextInput from '~/components/ui/TextInput';
import { isMaxLength } from '~/utils/common';

type UrlTextFieldProps = {
  urlValue: string;
  required?: boolean;
  onBlur?: Function;
  placeholder?: string;
  inputProps?: any;
  withoutUrlValidation?: boolean;
  validateImmediately?: boolean;
  onChange?: Function;
  requireProtocol?: boolean;
};
/**
 * @deprecated
 * Use `TextInput` instead.
 */
export default function UrlTextField({
  urlValue,
  required = true,
  onBlur,
  placeholder,
  inputProps,
  withoutUrlValidation,
  onChange,
  validateImmediately = true,
  requireProtocol = true,
  ...restProps
}: UrlTextFieldProps) {
  const [showError, setShowError] = useState(validateImmediately);
  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      if (!validateImmediately) {
        setShowError(false);
      }
    };
  }, []);

  const urlValueValidStart = requireProtocol ? urlValue.startsWith('http://') || urlValue.startsWith('https://') : true;
  const isHttpHttpsValid = required ? !!urlValue && urlValueValidStart : !urlValue || urlValueValidStart;
  const isRequiredAndNoValue = required && !isHttpHttpsValid && !urlValue;
  const isValidUrlProtocol = required ? !!urlValue && isHttpHttpsValid : !urlValue || isHttpHttpsValid;
  const isValidUrl = validator.isURL(urlValue, { require_protocol: requireProtocol }); // for localhost it will be not valid, because there is port, like :5173 and no Top-Level Domains (TLDs) (.com, .io, etc.)
  const isValidUrlWithoutValidation = !!urlValue && withoutUrlValidation;
  const isValidProtocolAndNoValidUrl = isValidUrlProtocol && !isValidUrl;

  const helperText = withoutUrlValidation
    ? !isValidUrlWithoutValidation && t('inputs:required')
    : (required && isRequiredAndNoValue && t('inputs:required')) ||
      (isHttpHttpsValid && isMaxLength(urlValue, inputProps?.maxLength || 2000) && t('errors:maxLength')) ||
      (!isValidUrlProtocol && t('errors:url_1')) ||
      (required
        ? isValidUrlProtocol && !isValidUrl && t('errors:url_2')
        : !!urlValue && isValidUrlProtocol && !isValidUrl && t('errors:url_2'));

  const error = withoutUrlValidation
    ? !isValidUrlWithoutValidation
    : isRequiredAndNoValue ||
      !isValidUrlProtocol ||
      (required ? isValidProtocolAndNoValidUrl : !!urlValue && isValidProtocolAndNoValidUrl);

  const handleBlur = () => {
    setShowError(true);
    if (onBlur) {
      onBlur({ isValid: !error });
    }
  };
  const handleChange = (event) => {
    setShowError(true);
    if (onChange) {
      onChange(event);
    }
  };

  return (
    <TextInput
      {...restProps}
      required={required}
      value={urlValue}
      error={restProps.error || (showError && error)}
      helperText={restProps.helperText || (showError && helperText)}
      placeholder={placeholder ?? 'https://www.example.com'}
      fullWidth
      onBlur={handleBlur}
      onChange={handleChange}
      inputProps={{ maxLength: inputProps?.maxLength || 2000 }}
    />
  );
}
